import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Instagram, 
  Facebook, 
  Music2, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  MessageSquare, 
  Clock, 
  Target, 
  Filter, 
  ArrowRight, 
  Zap, 
  Sparkles,
  BarChart3,
  X,
  Layers,
  MousePointer2,
  ChevronRight,
  RefreshCw,
  Inbox
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip 
} from 'recharts';
import './ContentRevenue.css';
import { getApiUrl } from '../../config/api';


const API_URL = getApiUrl('/analytics/content');
const getToken = () => localStorage.getItem('access_token') || '';


// Maps a detail_flow_steps label (e.g. "Post Reach", "DM Sent") to a display icon
// since the API only returns label/val/drop, no icon component.
const FLOW_ICON_MAP = {
  'post reach': Target,
  'reach': Target,
  'dm sent': MessageSquare,
  'dm': MessageSquare,
  'link click': MousePointer2,
  'click': MousePointer2,
  'purchase': DollarSign,
  'checkout': DollarSign,
};

const getFlowIcon = (label = '') => {
  const key = label.toLowerCase();
  const match = Object.keys(FLOW_ICON_MAP).find((k) => key.includes(k));
  return FLOW_ICON_MAP[match] || Target;
};


// Maps an insight's title to a display icon + accent color since the API only
// returns title/desc/metric, no icon component or color.
const INSIGHT_STYLE_MAP = [
  { keywords: ['reel', 'video', 'format'], icon: Layers, color: 'purple' },
  { keywords: ['hour', 'time', 'velocity', 'window'], icon: Clock, color: 'blue' },
  { keywords: ['intent', 'trigger', 'signal'], icon: Sparkles, color: 'orange' },
];

const getInsightStyle = (title = '', idx = 0) => {
  const key = title.toLowerCase();
  const match = INSIGHT_STYLE_MAP.find((entry) => entry.keywords.some((k) => key.includes(k)));
  if (match) return match;
  const fallbackPalette = [
    { icon: Sparkles, color: 'purple' },
    { icon: Layers, color: 'blue' },
    { icon: Clock, color: 'orange' },
  ];
  return fallbackPalette[idx % fallbackPalette.length];
};


const ContentRevenue = () => {
  const [activePlatform, setActivePlatform] = useState('All');
  const [contentType, setContentType] = useState('All');
  const [selectedItem, setSelectedItem] = useState(null);

  const [revenueData, setRevenueData] = useState(null);
  const [contentRanking, setContentRanking] = useState([]);
  const [detailFlowSteps, setDetailFlowSteps] = useState([]);
  const [revenueByTypeData, setRevenueByTypeData] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const fetchContentRevenue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(API_URL, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = await res.json();

      setRevenueData(data);
      setContentRanking(Array.isArray(data?.content_ranking) ? data.content_ranking : []);
      setDetailFlowSteps(Array.isArray(data?.detail_flow_steps) ? data.detail_flow_steps : []);
      setRevenueByTypeData(Array.isArray(data?.revenue_by_type) ? data.revenue_by_type : []);
      setInsights(Array.isArray(data?.insights) ? data.insights : []);
    } catch (err) {
      console.error('Content Revenue fetch error:', err);
      setError('Unable to load content revenue data from the server.');
      setRevenueData(null);
      setContentRanking([]);
      setDetailFlowSteps([]);
      setRevenueByTypeData([]);
      setInsights([]);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchContentRevenue();
  }, [fetchContentRevenue]);


  const filteredContent = useMemo(() => {
    return contentRanking.filter(item => {
      const platformMatch = activePlatform === 'All' || item.platform === activePlatform;
      const typeMatch = contentType === 'All' || item.type === contentType;
      return platformMatch && typeMatch;
    }).sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0));
  }, [contentRanking, activePlatform, contentType]);


  // Dynamic KPIs derived from API data or content items; strictly defaults to 0 when data cannot be loaded.
  const directRevenue = useMemo(() => {
    if (revenueData?.direct_content_revenue !== undefined && revenueData?.direct_content_revenue !== null) {
      return Number(revenueData.direct_content_revenue) || 0;
    }
    if (revenueData?.direct_revenue !== undefined && revenueData?.direct_revenue !== null) {
      return Number(revenueData.direct_revenue) || 0;
    }
    if (revenueData?.total_revenue !== undefined && revenueData?.total_revenue !== null) {
      return Number(revenueData.total_revenue) || 0;
    }
    if (revenueData?.stats?.direct_content_revenue !== undefined && revenueData?.stats?.direct_content_revenue !== null) {
      return Number(revenueData.stats.direct_content_revenue) || 0;
    }
    if (revenueData?.summary?.direct_content_revenue !== undefined && revenueData?.summary?.direct_content_revenue !== null) {
      return Number(revenueData.summary.direct_content_revenue) || 0;
    }
    if (contentRanking.length > 0) {
      return contentRanking.reduce((sum, item) => sum + (Number(item.revenue) || 0), 0);
    }
    return 0;
  }, [revenueData, contentRanking]);

  const directRevenueTrend = useMemo(() => {
    const val = revenueData?.direct_content_revenue_growth ??
      revenueData?.direct_revenue_growth ??
      revenueData?.revenue_growth ??
      revenueData?.direct_revenue_trend ??
      revenueData?.stats?.direct_revenue_trend ??
      revenueData?.summary?.direct_revenue_trend;
    return val !== undefined && val !== null ? Number(val) || 0 : 0;
  }, [revenueData]);

  const avgRevenuePerAsset = useMemo(() => {
    if (revenueData?.avg_revenue_per_asset !== undefined && revenueData?.avg_revenue_per_asset !== null) {
      return Number(revenueData.avg_revenue_per_asset) || 0;
    }
    if (revenueData?.avg_revenue !== undefined && revenueData?.avg_revenue !== null) {
      return Number(revenueData.avg_revenue) || 0;
    }
    if (revenueData?.stats?.avg_revenue_per_asset !== undefined && revenueData?.stats?.avg_revenue_per_asset !== null) {
      return Number(revenueData.stats.avg_revenue_per_asset) || 0;
    }
    if (revenueData?.summary?.avg_revenue_per_asset !== undefined && revenueData?.summary?.avg_revenue_per_asset !== null) {
      return Number(revenueData.summary.avg_revenue_per_asset) || 0;
    }
    if (contentRanking.length > 0) {
      return Math.round(directRevenue / contentRanking.length);
    }
    return 0;
  }, [revenueData, contentRanking, directRevenue]);

  const avgRevenueTrend = useMemo(() => {
    const val = revenueData?.avg_revenue_per_asset_growth ??
      revenueData?.avg_revenue_growth ??
      revenueData?.avg_revenue_trend ??
      revenueData?.stats?.avg_revenue_trend ??
      revenueData?.summary?.avg_revenue_trend;
    return val !== undefined && val !== null ? Number(val) || 0 : 0;
  }, [revenueData]);

  const conversionEfficiency = useMemo(() => {
    if (revenueData?.conversion_efficiency !== undefined && revenueData?.conversion_efficiency !== null) {
      return Number(revenueData.conversion_efficiency) || 0;
    }
    if (revenueData?.conv_efficiency !== undefined && revenueData?.conv_efficiency !== null) {
      return Number(revenueData.conv_efficiency) || 0;
    }
    if (revenueData?.conversion_rate !== undefined && revenueData?.conversion_rate !== null) {
      return Number(revenueData.conversion_rate) || 0;
    }
    if (revenueData?.conv_rate !== undefined && revenueData?.conv_rate !== null) {
      return Number(revenueData.conv_rate) || 0;
    }
    if (revenueData?.stats?.conversion_efficiency !== undefined && revenueData?.stats?.conversion_efficiency !== null) {
      return Number(revenueData.stats.conversion_efficiency) || 0;
    }
    if (revenueData?.summary?.conversion_efficiency !== undefined && revenueData?.summary?.conversion_efficiency !== null) {
      return Number(revenueData.summary.conversion_efficiency) || 0;
    }
    const itemsWithConv = contentRanking.filter(i => i.convRate !== undefined || i.conv_rate !== undefined);
    if (itemsWithConv.length > 0) {
      const sum = itemsWithConv.reduce((acc, i) => acc + (Number(i.convRate ?? i.conv_rate) || 0), 0);
      return Number((sum / itemsWithConv.length).toFixed(2));
    }
    return 0;
  }, [revenueData, contentRanking]);

  const conversionEfficiencyTrend = useMemo(() => {
    const val = revenueData?.conversion_efficiency_growth ??
      revenueData?.conv_rate_growth ??
      revenueData?.conversion_efficiency_trend ??
      revenueData?.stats?.conversion_efficiency_trend ??
      revenueData?.summary?.conversion_efficiency_trend;
    return val !== undefined && val !== null ? Number(val) || 0 : 0;
  }, [revenueData]);

  const avgPurchaseWindow = useMemo(() => {
    if (revenueData?.avg_purchase_window !== undefined && revenueData?.avg_purchase_window !== null) {
      return revenueData.avg_purchase_window;
    }
    if (revenueData?.purchase_window !== undefined && revenueData?.purchase_window !== null) {
      return revenueData.purchase_window;
    }
    if (revenueData?.avg_time_to_purchase !== undefined && revenueData?.avg_time_to_purchase !== null) {
      return revenueData.avg_time_to_purchase;
    }
    if (revenueData?.stats?.avg_purchase_window !== undefined && revenueData?.stats?.avg_purchase_window !== null) {
      return revenueData.stats.avg_purchase_window;
    }
    if (revenueData?.summary?.avg_purchase_window !== undefined && revenueData?.summary?.avg_purchase_window !== null) {
      return revenueData.summary.avg_purchase_window;
    }
    const itemsWithTime = contentRanking.filter(i => i.avgTimeToPurchase !== undefined || i.avg_time_to_purchase !== undefined);
    if (itemsWithTime.length > 0) {
      let count = 0;
      let hoursSum = 0;
      itemsWithTime.forEach(i => {
        const raw = String(i.avgTimeToPurchase ?? i.avg_time_to_purchase ?? '');
        const match = raw.match(/(\d+(\.\d+)?)/);
        if (match) {
          hoursSum += parseFloat(match[1]);
          count++;
        }
      });
      if (count > 0) {
        return `${(hoursSum / count).toFixed(1)} Hours`;
      }
    }
    return 0;
  }, [revenueData, contentRanking]);

  const formattedPurchaseWindow = useMemo(() => {
    if (!avgPurchaseWindow || avgPurchaseWindow === 0 || avgPurchaseWindow === '0') return '0 Hours';
    if (typeof avgPurchaseWindow === 'number') return `${avgPurchaseWindow} Hours`;
    if (typeof avgPurchaseWindow === 'string') {
      if (!avgPurchaseWindow.toLowerCase().includes('hour') && !avgPurchaseWindow.toLowerCase().includes('h')) {
        return `${avgPurchaseWindow} Hours`;
      }
      return avgPurchaseWindow;
    }
    return '0 Hours';
  }, [avgPurchaseWindow]);

  const purchaseWindowTrend = useMemo(() => {
    const val = revenueData?.avg_purchase_window_growth ??
      revenueData?.purchase_window_growth ??
      revenueData?.purchase_window_trend ??
      revenueData?.stats?.purchase_window_trend ??
      revenueData?.summary?.purchase_window_trend;
    return val !== undefined && val !== null ? Number(val) || 0 : 0;
  }, [revenueData]);

  const hasContent = filteredContent.length > 0;
  const hasFlowSteps = detailFlowSteps.length > 0;
  const hasRevenueByType = revenueByTypeData.length > 0;
  const hasInsights = insights.length > 0;

  const opportunityText = useMemo(() => {
    if (revenueData?.global_opportunity) return revenueData.global_opportunity;
    if (revenueData?.opportunity) return revenueData.opportunity;
    if (loading) return 'Analyzing content opportunities...';
    if (error || (!hasContent && !hasRevenueByType)) return '0 active opportunities detected.';

    if (revenueByTypeData.length > 0) {
      const topType = [...revenueByTypeData].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0))[0];
      if (topType && Number(topType.value) > 0) {
        return `${topType.name} yields highest revenue ($${Number(topType.value).toLocaleString()}) currently.`;
      }
    }

    if (insights.length > 0 && insights[0]?.desc) {
      return insights[0].desc;
    }

    return '0 active opportunities detected.';
  }, [revenueData, loading, error, hasContent, hasRevenueByType, revenueByTypeData, insights]);


  const getPlatformIcon = (platform) => {
    switch(platform) {
      case 'Instagram': return <Instagram size={14} />;
      case 'TikTok': return <Music2 size={14} />;
      case 'Facebook': return <Facebook size={14} />;
      default: return null;
    }
  };


  return (
    <>
      <Helmet>
        <title>Content Revenue - Revexia AI Revenue Brain</title>
        <meta name="description" content="Track revenue ROI of social media content" />
      </Helmet>


      <div className="content-revenue-wrapper">
        <div className="container-fluid px-4">
          {/* Header with Filters */}
          <header className="content-revenue-header">
            <p className="header-description">
              Identify the direct revenue ROI of your social media assets
            </p>
            
            <div className="header-filters">
              <div className="platform-filter-group">
                {['All', 'Instagram', 'TikTok', 'Facebook'].map(p => (
                  <button 
                    key={p}
                    onClick={() => setActivePlatform(p)}
                    className={`platform-filter-btn ${activePlatform === p ? 'active' : ''}`}
                  >
                    {p}
                  </button>
                ))}
              </div>


              <div className="content-type-filter-group">
                {['All', 'Reel', 'Post', 'Video'].map(t => (
                  <button 
                    key={t}
                    onClick={() => setContentType(t)}
                    className={`content-type-btn ${contentType === t ? 'active' : ''}`}
                  >
                    {t}
                  </button>
                ))}
              </div>


              {error && (
                <div className="fallback-notice">
                  <Inbox size={14} />
                  <span>{error}</span>
                </div>
              )}


              <button
                className="filter-icon-btn-content"
                onClick={fetchContentRevenue}
                disabled={loading}
                aria-label="Refresh content revenue data"
              >
                <RefreshCw size={20} className={loading ? 'spin' : ''} />
              </button>


              <button className="filter-icon-btn-content">
                <Filter size={20} />
              </button>
            </div>
          </header>


          {/* Stats Grid */}
          <section className="row g-4 mb-5">
            <div className="col-12 col-sm-6 col-xl-3">
              <div className="stat-card-content glow-orange">
                <div className="stat-card-glow orange"></div>
                <div className="stat-card-content-inner">
                  <div className="stat-card-icon orange">
                    <DollarSign size={20} />
                  </div>
                  <div className="stat-card-info">
                    <p className="stat-card-label">Direct Content Revenue</p>
                    <p className="stat-card-value">${Number(directRevenue).toLocaleString()}</p>
                    <div className={`stat-card-trend ${directRevenueTrend < 0 ? 'negative' : 'positive'}`}>
                      {directRevenueTrend < 0 ? <TrendingDown size={14} /> : <TrendingUp size={14} />} {Math.abs(directRevenueTrend)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <div className="col-12 col-sm-6 col-xl-3">
              <div className="stat-card-content glow-blue">
                <div className="stat-card-glow blue"></div>
                <div className="stat-card-content-inner">
                  <div className="stat-card-icon blue">
                    <BarChart3 size={20} />
                  </div>
                  <div className="stat-card-info">
                    <p className="stat-card-label">Avg. Revenue Per Asset</p>
                    <p className="stat-card-value">${Number(avgRevenuePerAsset).toLocaleString()}</p>
                    <div className={`stat-card-trend ${avgRevenueTrend < 0 ? 'negative' : 'positive'}`}>
                      {avgRevenueTrend < 0 ? <TrendingDown size={14} /> : <TrendingUp size={14} />} {Math.abs(avgRevenueTrend)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <div className="col-12 col-sm-6 col-xl-3">
              <div className="stat-card-content glow-purple">
                <div className="stat-card-glow purple"></div>
                <div className="stat-card-content-inner">
                  <div className="stat-card-icon purple">
                    <Zap size={20} />
                  </div>
                  <div className="stat-card-info">
                    <p className="stat-card-label">Conversion Efficiency</p>
                    <p className="stat-card-value">{conversionEfficiency}%</p>
                    <div className={`stat-card-trend ${conversionEfficiencyTrend < 0 ? 'negative' : 'positive'}`}>
                      {conversionEfficiencyTrend < 0 ? <TrendingDown size={14} /> : <TrendingUp size={14} />} {Math.abs(conversionEfficiencyTrend)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <div className="col-12 col-sm-6 col-xl-3">
              <div className="stat-card-content glow-cyan">
                <div className="stat-card-glow cyan"></div>
                <div className="stat-card-content-inner">
                  <div className="stat-card-icon cyan">
                    <Clock size={20} />
                  </div>
                  <div className="stat-card-info">
                    <p className="stat-card-label">Avg. Purchase Window</p>
                    <p className="stat-card-value">{formattedPurchaseWindow}</p>
                    <div className={`stat-card-trend ${purchaseWindowTrend < 0 ? 'negative' : 'positive'}`}>
                      {purchaseWindowTrend < 0 ? <TrendingDown size={14} /> : <TrendingUp size={14} />} {Math.abs(purchaseWindowTrend)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>


          {/* Charts Row */}
          <div className="row g-4 mb-5">
            <div className="col-12 col-lg-5">
              <div className="chart-card-revenue">
                <div className="chart-card-header">
                  <div>
                    <h4 className="chart-card-title">Revenue Distribution</h4>
                    <p className="chart-card-subtitle">Comparison of performance by content format</p>
                  </div>
                </div>
                <div className="chart-card-body">
                  {loading ? (
                    <div className="attribution-state-card">
                      <div className="loading-spinner"></div>
                      <p>Loading chart...</p>
                    </div>
                  ) : !hasRevenueByType ? (
                    <div className="no-data-block">
                      <Inbox size={24} />
                      <p>No revenue distribution data available</p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={revenueByTypeData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(13, 15, 41, 0.95)', 
                            border: '1px solid rgba(255,255,255,0.1)', 
                            borderRadius: '12px'
                          }}
                          itemStyle={{ color: '#fff' }}
                        />
                        <Bar dataKey="value" fill="#F97316" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-7">
              <div className="heatmap-placeholder">
                <BarChart3 size={48} className="heatmap-icon" />
                <p>Heatmap Visualization</p>
              </div>
            </div>
          </div>


          {/* Top Earning Assets Table */}
          <section className="top-earning-section">
            <div className="top-earning-glow"></div>
            
            <div className="top-earning-header">
              <div className="top-earning-header-left">
                <div className="pulse-indicator"></div>
                <h3 className="top-earning-title">Top Earning Assets</h3>
              </div>
              <span className="top-earning-badge">Top 50 Real-time</span>
            </div>


            {loading ? (
              <div className="attribution-state-card">
                <div className="loading-spinner"></div>
                <p>Loading content...</p>
              </div>
            ) : !hasContent ? (
              <div className="no-data-block">
                <Inbox size={26} />
                <p>No content assets match the current filters</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="content-table">
                  <thead>
                    <tr>
                      <th>Visual Asset</th>
                      <th>Attributed Revenue</th>
                      <th>Conversations</th>
                      <th className="text-end">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContent.map((item) => (
                      <tr 
                        key={item.id} 
                        onClick={() => setSelectedItem(item)}
                        className="content-table-row"
                      >
                        <td>
                          <div className="asset-cell">
                            <div className="asset-thumbnail">
                              <img src={item.thumbnail} alt={item.title} />
                              <div className="asset-thumbnail-overlay"></div>
                              <div className="asset-thumbnail-badge">
                                {getPlatformIcon(item.platform)}
                                <span>{item.type}</span>
                              </div>
                            </div>
                            <div className="asset-info">
                              <p className="asset-title">{item.title}</p>
                              <p className="asset-platform">{item.platform}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="revenue-value">${Number(item.revenue ?? 0).toLocaleString()}</span>
                        </td>
                        <td>
                          <div className="conversations-cell">
                            <div className="conversations-icon">
                              <MessageSquare size={16} />
                            </div>
                            <span className="conversations-count">{Number(item.dms ?? 0).toLocaleString()} Threads</span>
                          </div>
                        </td>
                        <td className="text-end">
                          <div className={`trend-badge ${(item.performance ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                            {(item.performance ?? 0) >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                            {Math.abs(item.performance ?? 0)}%
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>


          {/* AI Intelligence Layer */}
          <section className="ai-intelligence-section">
            <div className="ai-intelligence-header">
              <div>
                <h3 className="ai-intelligence-title">AI Intelligence Layer</h3>
                <p className="ai-intelligence-subtitle">Strategic optimizations identified by Revexia neural attribution engine</p>
              </div>
            </div>


            {loading ? (
              <div className="attribution-state-card">
                <div className="loading-spinner"></div>
                <p>Loading insights...</p>
              </div>
            ) : !hasInsights ? (
              <div className="no-data-block">
                <Inbox size={26} />
                <p>No AI insights available yet</p>
              </div>
            ) : (
              <div className="row g-4">
                {insights.map((insight, i) => {
                  const style = getInsightStyle(insight.title, i);
                  const Icon = style.icon;
                  const color = style.color;
                  return (
                    <div key={i} className="col-12 col-md-6 col-xl-4">
                      <div className={`insight-card-revenue insight-${color}`}>
                        <div className={`insight-card-glow ${color}`}></div>
                        
                        <div className="insight-card-top">
                          <div className={`insight-card-icon ${color}`}>
                            <Icon size={22} />
                          </div>
                          <span className={`insight-card-badge ${color}`}>Actionable</span>
                        </div>


                        <h4 className="insight-card-title">{insight.title}</h4>
                        <p className="insight-card-desc">{insight.desc}</p>


                        <div className="insight-card-footer">
                          <span className="insight-metric">{insight.metric}</span>
                          <button className={`insight-action-btn ${color}`}>
                            Apply Optimization <ArrowRight size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>


        {/* Detail Sidebar */}
        {selectedItem && (
          <>
            <div 
              className="detail-sidebar-overlay"
              onClick={() => setSelectedItem(null)}
            ></div>
            <div className="detail-sidebar">
              <div className="detail-sidebar-header">
                <div className="detail-sidebar-header-content">
                  <div className="detail-sidebar-thumbnail">
                    <img src={selectedItem.thumbnail} alt={selectedItem.title} />
                  </div>
                  <div className="detail-sidebar-info">
                    <div className="detail-sidebar-badges">
                      <span className="detail-badge-primary">Revenue Focus</span>
                      <span className="detail-badge-secondary">{selectedItem.platform}</span>
                    </div>
                    <h3 className="detail-sidebar-title">{selectedItem.title}</h3>
                    <p className="detail-sidebar-meta">
                      <Zap size={14} className="text-blue-400" /> {(selectedItem.performance ?? 0) > 0 ? 'High Performance' : 'Standard'} {selectedItem.type || 'Asset'}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedItem(null)}
                  className="detail-sidebar-close"
                >
                  <X size={28} />
                </button>
              </div>


              <div className="detail-sidebar-body">
                <div className="conversion-path-section">
                  <div className="conversion-path-header">
                    <h4 className="conversion-path-title">Diagnostic Conversion Path</h4>
                    {hasFlowSteps && <span className="conversion-path-badge">Active Flow</span>}
                  </div>
                  {!hasFlowSteps ? (
                    <div className="no-data-block compact">
                      <Inbox size={20} />
                      <p>No conversion path data available</p>
                    </div>
                  ) : (
                    <div className="flow-map-detail">
                      {detailFlowSteps.map((step, index) => {
                        const Icon = getFlowIcon(step.label);
                        return (
                          <React.Fragment key={index}>
                            <div className="flow-step-detail">
                              <div className="flow-step-icon-detail">
                                <Icon size={18} />
                              </div>
                              <div className="flow-step-info-detail">
                                <p className="flow-step-label-detail">{step.label}</p>
                                <p className="flow-step-value-detail">{step.val ?? 0}</p>
                              </div>
                              {step.drop && (
                                <div className="flow-step-drop-detail">-{step.drop}</div>
                              )}
                            </div>
                            {index < detailFlowSteps.length - 1 && (
                              <ChevronRight size={18} className="flow-connector-detail" />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>


                <div className="row g-4">
                  <div className="col-6">
                    <div className="metric-card-detail orange">
                      <div>
                        <p className="metric-card-label">Asset Lifetime Value</p>
                        <p className="metric-card-value">${Number(selectedItem.revenue ?? 0).toLocaleString()}</p>
                      </div>
                      <div className="metric-card-footnote">
                        {(() => {
                          const itemRev = Number(selectedItem.revenue ?? 0);
                          const avgRev = avgRevenuePerAsset || 0;
                          if (avgRev > 0) {
                            const diff = Math.round(((itemRev - avgRev) / avgRev) * 100);
                            return (
                              <>
                                {diff >= 0 ? (
                                  <TrendingUp size={16} className="text-emerald-400" />
                                ) : (
                                  <TrendingDown size={16} className="text-rose-400" />
                                )}
                                {Math.abs(diff)}% {diff >= 0 ? 'above' : 'below'} account average
                              </>
                            );
                          }
                          return (
                            <>
                              <TrendingUp size={16} className="text-muted" />
                              0% variance vs average
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="metric-card-detail blue">
                      <div>
                        <p className="metric-card-label">Customer Sentiment</p>
                        <p className="metric-card-value">
                          {selectedItem.sentiment ?? (selectedItem.performance !== undefined && selectedItem.performance !== null ? (selectedItem.performance > 0 ? 'Positive' : selectedItem.performance < 0 ? 'Negative' : 'Neutral') : '0')}
                        </p>
                      </div>
                      <div className="metric-card-footnote">
                        <Sparkles size={16} className="text-purple-400" />
                        {selectedItem.sentiment_note || (selectedItem.dms ? `${Number(selectedItem.dms).toLocaleString()} customer interactions` : '0 interactions recorded')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>


              <div className="detail-sidebar-footer">
                <button className="scale-similar-btn">
                  <Zap size={18} /> Scale Similar
                </button>
              </div>
            </div>
          </>
        )}


        {/* Bottom Action Panel */}
        {/* <div className="bottom-action-panel">
          <div className="bottom-action-content">
            <div className="bottom-action-left">
              <div className="bottom-action-icon">
                <BarChart3 size={24} />
              </div>
              <div>
                <p className="bottom-action-label">Global Opportunity</p>
                <p className="bottom-action-text">{opportunityText}</p>
              </div>
            </div>


            <div className="bottom-action-right">
              <button className="launch-campaign-btn">
                Launch Campaign <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div> */}
      </div>
    </>
  );
};


export default ContentRevenue;